import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { getSdk } from '@/lib/sdk';
import { toast } from 'sonner';
import { Mail, Lock, User, AtSign, Eye, EyeOff, ArrowRight, Loader2, Check, X } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Username availability
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check username availability with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = username.trim().toLowerCase();
    if (trimmed.length < 3) {
      setUsernameStatus('idle');
      return;
    }

    setUsernameStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const sdk = getSdk();
        const available = await sdk.checkUsernameAvailability(trimmed);
        setUsernameStatus(available ? 'available' : 'taken');
      } catch {
        setUsernameStatus('idle');
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password || !username.trim()) return;
    if (usernameStatus === 'taken') return;

    setIsSubmitting(true);
    try {
      await register({
        email: email.trim(),
        password,
        username: username.trim().toLowerCase(),
        displayName: displayName.trim() || undefined,
      });
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    email.trim().length > 0 &&
    password.length >= 8 &&
    username.trim().length >= 3 &&
    usernameStatus !== 'taken';

  return (
    <div className="min-h-screen bg-[#0B0D10] flex flex-col items-center justify-center px-6">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#A38BFF] to-[#7B68EE] flex items-center justify-center">
          <div className="w-7 h-7 rounded-full bg-[#0B0D10]/30" />
        </div>
        <h1 className="text-2xl font-bold text-[#F5F7FF] tracking-tight">Create Account</h1>
        <p className="text-sm text-[#A7B0C8] mt-1">Join Pulse today</p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        {/* Display Name (optional) */}
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
          <input
            type="text"
            placeholder="Display name (optional)"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            className="w-full h-12 pl-10 pr-4 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors"
          />
        </div>

        {/* Username */}
        <div className="relative">
          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            autoComplete="username"
            className={`w-full h-12 pl-10 pr-10 bg-[#141821] border rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none transition-colors ${
              usernameStatus === 'taken'
                ? 'border-red-500/50 focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30'
                : usernameStatus === 'available'
                  ? 'border-green-500/50 focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30'
                  : 'border-white/[0.08] focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30'
            }`}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {usernameStatus === 'checking' && (
              <Loader2 size={16} className="animate-spin text-[#A7B0C8]" />
            )}
            {usernameStatus === 'available' && <Check size={16} className="text-green-500" />}
            {usernameStatus === 'taken' && <X size={16} className="text-red-500" />}
          </div>
        </div>
        {usernameStatus === 'taken' && (
          <p className="text-xs text-red-400 -mt-2 pl-1">Username is already taken</p>
        )}

        {/* Email */}
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className="w-full h-12 pl-10 pr-4 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors"
          />
        </div>

        {/* Password */}
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]" size={18} />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password (min. 8 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full h-12 pl-10 pr-12 bg-[#141821] border border-white/[0.08] rounded-xl text-[#F5F7FF] placeholder:text-[#A7B0C8]/50 focus:outline-none focus:border-[#A38BFF]/50 focus:ring-1 focus:ring-[#A38BFF]/30 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A7B0C8] hover:text-[#F5F7FF]"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {password.length > 0 && password.length < 8 && (
          <p className="text-xs text-[#A7B0C8] -mt-2 pl-1">At least 8 characters required</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className="w-full h-12 bg-gradient-to-r from-[#A38BFF] to-[#7B68EE] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
        >
          {isSubmitting ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <>
              Create Account
              <ArrowRight size={18} />
            </>
          )}
        </button>

        {/* Login link */}
        <p className="mt-6 text-center text-sm text-[#A7B0C8]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#A38BFF] hover:underline font-medium">
            Sign In
          </Link>
        </p>
      </form>
    </div>
  );
}
